import {useContext, useEffect, useMemo, useState} from 'react';
import Select from 'react-select';

import {CircularProgress, FormControlLabel, Switch} from '@mui/material';

import GeneralLeaderboardTable from './GeneralLeaderboardTable';
import {dropdownStylesConfig} from '../../components/form/dropdown/Dropdown';
import {Group} from '../../types/entities/Group';
import {request, sendGetRequest, URLs} from '../../api';
import AppContext from '../../context/AppContext';
import {GetGroupGeneralLeaderboardResponse} from '../../api/responses/GroupsResponseTypes';
import GeneralLeaderboardExportGradesButton from './GeneralLeaderboardExportGradesButton';
import {ModuleRoleCode, Role} from '../../types/entities/Role';

function GeneralLeaderboardPage() {
  const {user, dispatchError} = useContext(AppContext);

  const dropdownStyles = dropdownStylesConfig();

  const [groups, setGroups] = useState<Array<Group>>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  const [leaderboardData, setLeaderboardData] = useState<GetGroupGeneralLeaderboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [duringContest, setDuringContest] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  useEffect(() => {
    sendGetRequest(URLs.accessibleUserModuleGroups(user!.activeModule!.moduleId, true))
      .then((response) => {
        const allActiveAvailableGroups: Array<Group> = response.data || [];

        if (allActiveAvailableGroups.length === 1) {
          setSelectedGroup(allActiveAvailableGroups[0]);
        }

        setGroups(allActiveAvailableGroups);
      })
      .catch((err) => {
        if (err) {
          dispatchError(err);
        }
      });
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      setLeaderboardData(null);
      getGroupLeaderboard();
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (selectedGroup) {
      getGroupLeaderboard();
    }
  }, [page, rowsPerPage, duringContest]);

  const getGroupLeaderboard = () => {
    if (selectedGroup) {
      setIsLoading(true);
      request<{}, {}, GetGroupGeneralLeaderboardResponse>(
        URLs.getGroupGeneralLeaderboard(selectedGroup.id, page, rowsPerPage, duringContest),
        {
          method: 'GET',
          successCallback: (response) => {
            setLeaderboardData(response);
            setPage(response.metadata.page);
            setRowsPerPage(response.metadata.limit);
            setIsLoading(false);
          },
          errorCallback: (e: any) => {
            dispatchError({
              errorMessage: e.response.data.error ?? 'There was an error. Please try again later.',
              redirectURL: '/contests',
            });
          },
        }
      );
    }
  };

  const handlePageNoChange = (value: number) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
  };

  const groupOptions = useMemo(
    () => (groups ? groups.map((group) => ({value: group.id, label: group.name})) : []),
    [groups]
  );

  return (
    <div className="flex flex-col justify-normal w-full h-[calc(100vh-7rem)] p-4 gap-8">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-4 w-full">
          <div className="flex flex-row w-full max-w-[400px] justify-self-start">
            <Select
              id="select-group"
              placeholder="Select group..."
              defaultValue={groupOptions.find((option) => option.value == selectedGroup?.id)}
              onChange={(event) => {
                const selectedId: string = (event as {value: string; label: string}).value;
                const group = groups.filter((gr) => gr.id === Number(selectedId))[0];
                setSelectedGroup(group);
              }}
              value={selectedGroup ? {value: selectedGroup.id, label: selectedGroup.name} : null}
              options={groupOptions}
              styles={dropdownStyles}
              isDisabled={groupOptions.length === 1}
            />
          </div>
          <div className="select-none">
            <FormControlLabel
              control={
                <Switch
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDuringContest(event.target.checked)}
                  checked={duringContest}
                />
              }
              label="During contest"
            />
          </div>
        </div>
        <div className="flex flex-row justify-end w-full">
          {(user?.activeModule?.role === ModuleRoleCode.COURSE_ADMIN ||
            user?.activeModule?.role === ModuleRoleCode.TEACHER ||
            user?.activeModule?.role === ModuleRoleCode.AUTHOR_TEACHER ||
            user?.role === Role.ADMIN) && (
            <GeneralLeaderboardExportGradesButton
              groupId={selectedGroup ? selectedGroup.id : null}
              duringContest={duringContest}
            />
          )}
        </div>
      </div>
      {selectedGroup && leaderboardData && (
        <GeneralLeaderboardTable
          data={leaderboardData}
          isLoading={isLoading}
          totalEntries={leaderboardData.metadata.total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageNoChange={handlePageNoChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
      {!selectedGroup && !isLoading && !leaderboardData && (
        <div className="text-center text-3xl text-gray-300 font-bold ">
          To get started, please select a group from the list to display its leaderboard and see how members are
          ranking.
        </div>
      )}
      {selectedGroup && isLoading && !leaderboardData && (
        <div className="text-center text-3xl text-gray-300 font-bold ">
          <CircularProgress size={'84px'} color="success" />
        </div>
      )}
    </div>
  );
}

export default GeneralLeaderboardPage;
